/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import { Typography, Tooltip } from '@mui/material';
import { blue, grey, pink } from '@mui/material/colors';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Button from '@mui/material/Button';
import { useLongPress } from 'use-long-press';
import { Household, VisitStatus } from '../../types';
import { useLiveCard } from '../../services/liveCard';

enum ButtonVariants { // placeholder name
  contained = 'contained',
  outlined = 'outlined',
  text = 'text',
}

enum ButtonColors {
  inherit = 'inherit',
  primary = 'primary',
  secondary = 'secondary',
  success = 'success',
  error = 'error',
  info = 'info',
  warning = 'warning',
}

interface HouseHoldButtonProps {
  household: Household;
  size?: 'small' | 'medium' | 'large' | undefined;
  onClick?: (id: number) => () => void;
  onLongClick?: (household: Household) => void;
}

export function HouseHoldButton({
  household,
  size = 'medium',
  onClick = () => () => {},
  onLongClick = () => {},
}: HouseHoldButtonProps) {
  let variant = ButtonVariants.text;
  let color = ButtonColors.primary;
  let sx = {};

  const { id, name } = household;
  const live = useLiveCard();
  // Live overlay: prefer a freshly-synced status from co-viewers over the
  // locally-held one, so the button reflects others' taps without a refresh.
  const status = live.getStatus(id) ?? household.status;
  const editor = live.editorOf(id); // another person currently touching this one

  switch (status) {
    case VisitStatus.intact:
      variant = ButtonVariants.outlined;
      color = ButtonColors.primary;
      break;
    case VisitStatus.visited:
      variant = ButtonVariants.contained;
      color = ButtonColors.primary;
      sx = { background: blue[800] };
      break;
    case VisitStatus.met:
      variant = ButtonVariants.outlined;
      color = ButtonColors.primary;
      sx = { color: grey[200], borderColor: grey[200] };
      break;
    case VisitStatus.noVisit:
      variant = ButtonVariants.outlined;
      color = ButtonColors.error;
      break;
    default:
  }

  // Presence ring: someone else is editing this household right now.
  if (editor) {
    sx = {
      ...sx,
      outline: `2px solid ${pink.A400}`,
      outlineOffset: 1,
    };
  }

  const bind = useLongPress(() => {
    onLongClick(household);
  });

  const button = (
    <Button
      sx={{ pl: 1, pr: 1, ...sx }}
      variant={variant}
      color={color}
      size={size}
      // style={{ width: 90 }}
      onClick={onClick?.(id)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...bind()}
    >
      <Typography variant="subtitle1">{name}</Typography>
    </Button>
  );

  if (editor) {
    return (
      <Tooltip title={`${editor} 님 편집 중`} arrow open placement="top">
        {button}
      </Tooltip>
    );
  }
  return button;
}

export function MultiOpsIconButton(props: any) {
  const { iconName, onClick, visible } = props;
  let icon;
  switch (iconName) {
    case 'edit':
      icon = <EditIcon fontSize="small" />;
      break;
    case 'delete':
      icon = <DeleteIcon fontSize="small" />;
      break;
    case 'lock':
      icon = <LockIcon fontSize="small" />;
      break;
    case 'add':
      icon = <AddBoxOutlinedIcon fontSize="small" />;
      break;
    default:
      icon = <LockIcon fontSize="small" />;
  }
  if (visible) {
    return (
      <IconButton size="small" onClick={onClick}>
        {icon}
      </IconButton>
    );
  }
  return null;
}

export function ToggleEditButton(props: any) {
  const { id, isLock, onClick, visible } = props;

  if (isLock) {
    return (
      <MultiOpsIconButton
        visible={visible}
        iconName="edit"
        onClick={onClick(id)}
      />
    );
  }
  return (
    <MultiOpsIconButton
      visible={visible}
      iconName="lock"
      onClick={onClick(id)}
    />
  );
}
